/**
 * Daily Log Service
 * Handles daily project logging API calls
 */

import { httpClient, ApiResponse } from './http.client';

// =================== INTERFACES ===================

export interface DailyLog {
  daily_log_id: string;
  organization_id: string;
  project_id: string;
  log_date: string;
  log_number: string;
  log_title?: string;
  foreman?: string;

  // Weather Information
  weather_conditions?: string;
  temperature_high?: number;
  temperature_low?: number;
  wind_conditions?: string;
  site_conditions?: string;

  // Work Progress
  work_performed?: string;
  work_completed_percentage?: number;
  areas_worked?: string;

  // Activities
  scheduled_activities?: string;
  completed_activities?: string;
  delayed_activities?: string;
  reason_for_delays?: string;

  // Crew & Equipment
  total_workers_on_site?: number;
  contractors_on_site?: string;
  equipment_on_site?: string;
  equipment_issues?: string;

  // Materials
  materials_delivered?: string;
  materials_used?: string;
  material_shortages?: string;

  // Safety & Quality
  safety_incidents?: string | null;
  safety_meetings_held?: boolean;
  quality_issues?: string;
  inspections_conducted?: string;

  // Visitors & Meetings
  visitors_on_site?: string;
  meetings_held?: string;

  // Issues & Follow-up
  problems_encountered?: string;
  corrective_actions?: string;
  follow_up_required?: string;

  // Photos
  photos_taken?: number;
  photo_storage_path?: string;

  // Metadata
  created_by_user_id?: string;
  reviewed_by_user_id?: string | null;
  approved_by_user_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface DailyLogCreate {
  project_id: string;
  organization_id: string;
  log_date: string;
  log_number: string;
  log_title?: string;
  foreman?: string;

  weather_conditions?: string;
  temperature_high?: number;
  temperature_low?: number;
  wind_conditions?: string;
  site_conditions?: string;

  work_performed?: string;
  work_completed_percentage?: number;
  areas_worked?: string;

  scheduled_activities?: string;
  completed_activities?: string;
  delayed_activities?: string;
  reason_for_delays?: string;

  total_workers_on_site?: number;
  contractors_on_site?: string;
  equipment_on_site?: string;
  equipment_issues?: string;

  materials_delivered?: string;
  materials_used?: string;
  material_shortages?: string;

  safety_incidents?: string | null;
  safety_meetings_held?: boolean;
  quality_issues?: string;
  inspections_conducted?: string;

  visitors_on_site?: string;
  meetings_held?: string;

  problems_encountered?: string;
  corrective_actions?: string;
  follow_up_required?: string;

  photos_taken?: number;
  photo_storage_path?: string;
}

export interface DailyLogUpdate {
  weather_conditions?: string;
  temperature_high?: number;
  temperature_low?: number;
  wind_conditions?: string;
  site_conditions?: string;

  work_performed?: string;
  work_completed_percentage?: number;
  areas_worked?: string;

  scheduled_activities?: string;
  completed_activities?: string;
  delayed_activities?: string;
  reason_for_delays?: string;

  total_workers_on_site?: number;
  contractors_on_site?: string;
  equipment_on_site?: string;
  equipment_issues?: string;

  materials_delivered?: string;
  materials_used?: string;
  material_shortages?: string;

  safety_incidents?: string | null;
  safety_meetings_held?: boolean;
  quality_issues?: string;
  inspections_conducted?: string;

  visitors_on_site?: string;
  meetings_held?: string;

  problems_encountered?: string;
  corrective_actions?: string;
  follow_up_required?: string;

  photos_taken?: number;
  photo_storage_path?: string;
}

export interface DailyLogActivity {
  activity_id: string;
  organization_id: string;
  daily_log_id: string;
  activity_title?: string;
  activity_description: string;
  location_area?: string;

  quantity_completed?: number;
  unit_of_measure?: string;
  percentage_complete?: number;

  start_time?: string;
  end_time?: string;

  workers_assigned?: number;
  hours_worked?: number;
  equipment_used?: string;

  boq_item_id?: string;
  scheduled_task_id?: string;
  cost_code_id?: string;

  labour_cost?: number;
  materials_cost?: number;
  equipment_cost?: number;

  is_completed?: boolean;
  is_delayed?: boolean;
  delay_reason?: string | null;

  quality_notes?: string;
  safety_notes?: string;

  materials_consumed?: string;
  material_waste_notes?: string;

  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DailyLogActivityCreate {
  organization_id: string;
  activity_title?: string;
  activity_description: string;
  location_area?: string;

  quantity_completed?: number;
  unit_of_measure?: string;
  percentage_complete?: number;

  start_time?: string;
  end_time?: string;

  workers_assigned?: number;
  hours_worked?: number;
  equipment_used?: string;

  boq_item_id?: string;
  scheduled_task_id?: string;
  cost_code_id?: string;

  labour_cost?: number;
  materials_cost?: number;
  equipment_cost?: number;

  is_completed?: boolean;
  is_delayed?: boolean;
  delay_reason?: string | null;

  quality_notes?: string;
  safety_notes?: string;

  materials_consumed?: string;
  material_waste_notes?: string;

  notes?: string;
}

export interface DailyLogActivityUpdate {
  activity_title?: string;
  activity_description?: string;
  location_area?: string;

  quantity_completed?: number;
  unit_of_measure?: string;
  percentage_complete?: number;

  start_time?: string;
  end_time?: string;

  workers_assigned?: number;
  hours_worked?: number;
  equipment_used?: string;

  boq_item_id?: string;
  scheduled_task_id?: string;
  cost_code_id?: string;

  labour_cost?: number;
  materials_cost?: number;
  equipment_cost?: number;

  is_completed?: boolean;
  is_delayed?: boolean;
  delay_reason?: string | null;

  quality_notes?: string;
  safety_notes?: string;

  materials_consumed?: string;
  material_waste_notes?: string;

  notes?: string;
}

export interface DailyLogFilters {
  project_id?: string;
  start_date?: string;
  end_date?: string;
  skip?: number;
  limit?: number;
}

// =================== DAILY LOG SERVICE CLASS ===================

class DailyLogService {
  /**
   * Create new daily log
   */
  async createDailyLog(logData: DailyLogCreate): Promise<ApiResponse<DailyLog>> {
    return httpClient.post<DailyLog>('/api/v1/daily-logs/', logData);
  }

  /**
   * Update daily log
   */
  async updateDailyLog(dailyLogId: string, logData: DailyLogUpdate): Promise<ApiResponse<DailyLog>> {
    return httpClient.put<DailyLog>(`/api/v1/daily-logs/${dailyLogId}/`, logData);
  }

  /**
   * Get daily logs with filters
   */
  async getDailyLogs(filters?: DailyLogFilters): Promise<ApiResponse<DailyLog[]>> {
    const queryParams = new URLSearchParams();

    if (filters) {
      if (filters.project_id) queryParams.append('project_id', filters.project_id);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.skip !== undefined) queryParams.append('skip', filters.skip.toString());
      if (filters.limit !== undefined) queryParams.append('limit', filters.limit.toString());
    }

    const queryString = queryParams.toString();
    return httpClient.get<DailyLog[]>(`/api/v1/daily-logs/${queryString ? '?' + queryString : ''}`);
  }

  /**
   * Get single daily log
   */
  async getDailyLog(dailyLogId: string): Promise<ApiResponse<DailyLog>> {
    return httpClient.get<DailyLog>(`/api/v1/daily-logs/${dailyLogId}/`);
  }

  /**
   * Delete daily log
   */
  async deleteDailyLog(dailyLogId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/daily-logs/${dailyLogId}/`);
  }

  // =================== ACTIVITY OPERATIONS ===================

  /**
   * Create activity for a daily log
   */
  async createDailyLogActivity(
    dailyLogId: string,
    activityData: DailyLogActivityCreate
  ): Promise<ApiResponse<DailyLogActivity>> {
    return httpClient.post<DailyLogActivity>(
      `/api/v1/daily-logs/${dailyLogId}/activities/`,
      activityData
    );
  }

  /**
   * Get activities for a daily log
   */
  async getDailyLogActivities(dailyLogId: string): Promise<ApiResponse<DailyLogActivity[]>> {
    return httpClient.get<DailyLogActivity[]>(`/api/v1/daily-logs/${dailyLogId}/activities/`);
  }

  /**
   * Update activity
   */
  async updateDailyLogActivity(
    activityId: string,
    activityData: DailyLogActivityUpdate
  ): Promise<ApiResponse<DailyLogActivity>> {
    return httpClient.put<DailyLogActivity>(
      `/api/v1/daily-logs/activities/${activityId}/`,
      activityData
    );
  }

  /**
   * Delete activity
   */
  async deleteDailyLogActivity(activityId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/daily-logs/activities/${activityId}/`);
  }

  // =================== UTILITY METHODS ===================

  /**
   * Get daily logs for a specific project
   */
  async getProjectDailyLogs(
    projectId: string,
    startDate?: string,
    endDate?: string,
    skip = 0,
    limit = 50
  ): Promise<ApiResponse<DailyLog[]>> {
    return this.getDailyLogs({
      project_id: projectId,
      start_date: startDate,
      end_date: endDate,
      skip,
      limit
    });
  }

  /**
   * Get daily log by project and date
   */
  async getDailyLogByDate(projectId: string, date: string): Promise<ApiResponse<DailyLog | null>> {
    const response = await this.getDailyLogs({
      project_id: projectId,
      start_date: date,
      end_date: date,
      limit: 1
    });

    if (response.data && response.data.length > 0) {
      return {
        ...response,
        data: response.data[0]
      };
    }

    return {
      ...response,
      data: null
    };
  }
}

// Export singleton instance
export const dailyLogService = new DailyLogService();
